/**
 * Toast Utilities - Centralized toast function availability
 * Eliminates the need for polling in individual components
 */

/**
 * Ensures toast functions are available without polling
 * Returns immediately if functions exist, or provides a proper promise with timeout
 * 
 * @param {number} timeout - Maximum time to wait in milliseconds (default: 5000)
 * @returns {Promise<boolean>} - Resolves to true when functions are available, false on timeout
 */
export function ensureToastFunctions(timeout = 5000) {
  return new Promise((resolve) => {
    // Check if functions are already available
    if (typeof window.showError !== 'undefined' && 
        typeof window.showSuccess !== 'undefined' && 
        typeof window.showWarning !== 'undefined' && 
        typeof window.showInfo !== 'undefined') {
      resolve(true);
      return;
    }

    // If not available, set up a single timeout check
    let timeoutId = null;
    let intervalId = null;
    let resolved = false;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Set timeout to fail after specified time
    timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        console.warn('Toast functions not available after timeout. Using fallback.');
        resolve(false);
      }
    }, timeout);

    // Check periodically, but with reasonable interval and cleanup
    intervalId = setInterval(() => {
      if (typeof window.showError !== 'undefined' && 
          typeof window.showSuccess !== 'undefined' && 
          typeof window.showWarning !== 'undefined' && 
          typeof window.showInfo !== 'undefined') {
        if (!resolved) {
          resolved = true;
          cleanup();
          resolve(true);
        }
      }
    }, 100); // Check every 100ms, but with proper cleanup

    // Register cleanup with memory leak prevention
    if (window.memoryLeakPrevention && typeof window.memoryLeakPrevention.addTimeout === 'function') {
      window.memoryLeakPrevention.addTimeout('ToastUtils', timeoutId, 'ensureToastFunctions timeout');
      window.memoryLeakPrevention.addInterval('ToastUtils', intervalId, 'ensureToastFunctions interval');
    }
  });
}

/**
 * Safe toast function caller - calls toast if available, logs to console if not
 * 
 * @param {string} type - Toast type: 'success', 'error', 'warning', 'info'
 * @param {string} message - Message to display
 * @param {Object} config - Toast configuration
 */
export function safeToast(type, message, config = {}) {
  const functionMap = {
    success: window.showSuccess,
    error: window.showError,
    warning: window.showWarning,
    info: window.showInfo
  };

  const toastFunction = functionMap[type];
  
  if (typeof toastFunction === 'function') {
    toastFunction(message, config);
  } else {
    // Fallback to console if toast not available
    const logMap = {
      success: 'log',
      error: 'error', 
      warning: 'warn',
      info: 'info'
    };
    
    const logMethod = logMap[type] || 'log';
    console[logMethod](`[${type.toUpperCase()}] ${message}`);
  }
}

/**
 * Initialize toast utils - call this early in app lifecycle
 * This ensures toast functions are available immediately
 */
export function initializeToastUtils() {
  // Import toast system to ensure it's loaded
  import('../components/common/Toast.js').then(() => {
    console.log('✅ Toast system initialized via ToastUtils');
  }).catch(error => {
    console.error('❌ Failed to initialize toast system:', error);
  });
}

export default {
  ensureToastFunctions,
  safeToast,
  initializeToastUtils
};
